import { Card, CardContent, Grid, Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as coreApi from "api/Core";
import { FeedClassKey } from "assets/jss/common-styles";
import IconButtonClose from "components/Buttons/IconButtonClose";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestWrapIndicator } from "components/CallServer/RequestWrapIndicator";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiLink from "components/MuiExtras/MuiLink";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import UserFullProfileLink from "components/UserFullProfileLink";
import { PublicationVisility } from "models/ApiEnums";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { SidebarPostWindowMessages } from "store/sidebar/windowMessages";
import { ICardHiddenProps } from "views/Timeline/Components/CardLib/ICardProps";
import AbuseReportDrawer from "views/Timeline/Dialogs/AbuseReportDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    mouseOver: boolean;
    showBody: boolean;
    dialog: any;
    lastError: any;
}

type Props = ICardHiddenProps & WithSnackbar & WithTranslation & StyledComponentProps<FeedClassKey>;

/////////////////////////////////////////////////////////////////////////////////////

class HideCard extends React.PureComponent<Props, IState> {
    private sidebarWindowMessages: SidebarPostWindowMessages;

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            mouseOver: false,
            showBody: null,
            lastError: null
        };

        this.sidebarWindowMessages = new SidebarPostWindowMessages(window.opener || window);
    }

    ///
    private handleUndo = () => {
        this.props.onChangePublicationVisibility(PublicationVisility.Visible)
            .catch((error) => this.setState({ lastError: error }));
    };

    ///
    private handleClose = () => {
        this.props.onChangePublicationVisibility(PublicationVisility.None)
            .then(() => this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.HideSuccess"), "success"))
            .catch((error) => this.setState({ lastError: error }));
    };

    ///
    private handleAbuseReport = () => {
        this.setState({
            dialog: (
                <AbuseReportDrawer
                    publicationId={this.props.item.publicationId}
                    onClose={
                        (feedItem) => {
                            this.setState({ dialog: null }, () => {
                                if (feedItem != null) {
                                    this.props.onRemoveCardFromFeed(this.props.item.publicationId);
                                    this.props.snackbar.showMessage("Common.Feed.Messages.ReportAbuseSuccess", "success");
                                }
                            });
                        }
                    }
                />
            )
        });
    };

    ///
    private handleFollowTopic = () => {
        const { item: { publication: { topics, mainTopicId } } } = this.props;

        this.setState({
            dialog: (
                <MuiConfirmation
                    message={
                        <Trans
                            i18nKey="Common.Feed.Hide.ConfirmationUnfollowMessage"
                            values={{ name: topics.find(x => x.key == mainTopicId).label }}
                            components={{ b: <strong /> }}
                        />
                    }
                    onClose={
                        (result) => {
                            if (result) {
                                coreApi.followTopic(this.props.item.publication.mainTopicId, false)
                                    .then(() => {
                                        this.props.onRemoveCardFromFeed(this.props.item.publicationId);
                                        this.sidebarWindowMessages.reset();

                                        this.setState({ dialog: null });
                                    })
                                    .catch((error) => this.setState({ dialog: null, lastError: error }));
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });
    };

    ///
    private handleSilenceTopic = () => {
        const { item: { publication: { topics, mainTopicId } } } = this.props;

        this.setState({
            dialog: (
                <MuiConfirmation
                    message={this.props.t("Common.Feed.Messages.SilienceTopicMessage", { name: topics.find(x => x.key == mainTopicId).label })}
                    onClose={
                        (result) => {
                            if (result) {
                                coreApi.userMuteTopic(mainTopicId)
                                    .then(() => {
                                        this.props.onRemoveCardFromFeed(this.props.item.publicationId);

                                        this.setState({ dialog: null });

                                        this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.SilienceTopicSuccess"), "success");

                                    })
                                    .catch((error) => this.setState({ dialog: null, lastError: error }));
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });
    };

    ///
    private handleSilenceAuthor = () => {
        const { item: { publication: { publisher } } } = this.props;

        this.setState({
            dialog: (
                <MuiConfirmation
                    message={
                        <Trans
                            i18nKey="Common.Feed.Hide.ConfirmationSilienceAuthorMessage"
                            values={{ name: publisher.name }}
                            components={{ b: <strong /> }}
                        />
                    }
                    onClose={
                        (result) => {
                            if (result) {
                                coreApi.userMuteAuthor(publisher.id)
                                    .then(() => {

                                        this.props.onRemoveCardFromFeed(this.props.item.publicationId);

                                        this.setState({ dialog: null }, () => {
                                            this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.SilienceAuthorSuccess"), "success");
                                        });

                                    })
                                    .catch((error) => this.setState({ dialog: null, lastError: error }));
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });

    };

    ///
    public render() {
        const {
            classes,
            item,
        } = this.props;

        return (
            <>
                <Card
                    id={item.publicationId}
                    className={classes.card}
                >

                    <RequestWrapIndicator enabled={item.isUpdating} type="update">

                        <CardContent className={classes.cardContent}>

                            <Grid container justifyContent="space-between" >
                                <Grid item zeroMinWidth>
                                    <Typography color="inherit" variant="caption">
                                        <span><b>{this.props.t("Common.Feed.Hide.Title")}</b> </span>
                                    </Typography>
                                    <Typography color="inherit" variant="subtitle1">
                                        <MuiLink color="secondary" href={null} onClick={this.handleUndo}>{this.props.t("Common.Feed.Hide.Undo")}</MuiLink>
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <IconButtonClose onClick={this.handleClose} />
                                </Grid>
                            </Grid>

                            <p>
                                <b>{this.props.t("Common.Feed.Hide.PublicationOptionsHeader")}</b>
                                <ul>
                                    {
                                        item.interaction.followingAuthor &&
                                        <li>
                                            <MuiLink color="secondary" onClick={this.handleSilenceAuthor}>{this.props.t("Common.Feed.Hide.PublicationOptionSilienceAuthor")}</MuiLink>&nbsp;&nbsp;(<UserFullProfileLink user={item.publication.publisher} />)
                                        </li>
                                    }
                                    <li>
                                        <MuiLink color="secondary" onClick={this.handleAbuseReport}>{this.props.t("Common.Feed.Hide.PublicationOptionReportAbuse")}</MuiLink>
                                    </li>
                                </ul>
                            </p>

                            {
                                item.interaction.followingMainTopic &&
                                <p>
                                    <b>{this.props.t("Common.Feed.Hide.TopicOptionsHeader")}</b>
                                    <ul>
                                        <li>
                                            <MuiLink color="secondary" onClick={this.handleSilenceTopic}>{this.props.t("Common.Feed.Hide.TopicOptionSilience")}</MuiLink>
                                        </li>
                                        <li>
                                            <MuiLink color="secondary" onClick={this.handleFollowTopic}>{this.props.t("Common.Feed.Hide.TopicOptionUnfollow")}</MuiLink>
                                        </li>
                                    </ul>
                                </p>
                            }
                        </CardContent>

                    </RequestWrapIndicator>
                </Card>

                {this.state.dialog}

                <RequestErrorDialog
                    open={this.state.lastError != null}
                    error={this.state.lastError}
                    onClose={() => this.setState({ lastError: null })}
                />

            </>
        );
    }
}

export default withSnackbar(withTranslation()(HideCard));
